import { Select } from 'antd';
import React, { CSSProperties, useEffect, useState } from 'react';
import { ID } from '../../../global';
import { trueFilter } from '@/utils/common';
import { DataSource } from './datasource';
import { useSafeState } from '../../../api/hooks';

// 使用条件类型定义 onChange 参数
type SelectorOnChangeType<
  T,
  M extends boolean | undefined,
  U extends boolean | undefined,
> = M extends true
  ? U extends true
    ? (val: T[]) => void
    : (val: ID[]) => void
  : U extends true
    ? (val: T) => void
    : (val: ID) => void;

// 定义 ValueType 类型
type ValueType<T, M extends boolean | undefined, U extends boolean | undefined> = M extends true
  ? U extends true
    ? T[]
    : ID[]
  : U extends true
    ? T
    : ID;

type SelectorProps<
  T,
  M extends boolean | undefined = undefined,
  U extends boolean | undefined = undefined,
> = {
  value?: ValueType<T, M, U>;
  onChange?: SelectorOnChangeType<T, M, U>;
  multiple?: M;
  placeholder?: string;
  useEntity?: U;
  selectStyle?: CSSProperties;
  selectClassName?: string;
  dataFilter?: (i: T) => boolean;
};

export const createSelector = <T,>({ datasource }: { datasource: DataSource<T> }) => {
  return function SelectorComponent<
    M extends boolean | undefined = false,
    U extends boolean | undefined = false,
  >({
    value,
    onChange,
    multiple,
    placeholder,
    useEntity,
    selectStyle,
    selectClassName,
    dataFilter = trueFilter,
  }: SelectorProps<T, M, U>) {
    const [options, setOptions] = useSafeState<{ label: React.ReactNode; value: ID }[]>([]);
    const [valueMap, setValueMap] = useSafeState<Map<ID, T>>(new Map());
    const [loading, setLoading] = useSafeState(false);

    useEffect(() => {
      setLoading(true);
      datasource
        .getList()
        .then(list => {
          list = list.filter(dataFilter);
          const optionList = list.map(item => {
            const id = datasource.getValue(item);
            return {
              label: datasource.getLabel(item),
              value: id,
            };
          });
          const map = new Map<ID, T>();
          list.forEach(item => {
            map.set(datasource.getValue(item), item);
          });
          setOptions(optionList);
          setValueMap(map);
        })
        .finally(() => setLoading(false));
    }, []);

    // 将 value 统一转成 ID
    const toID = (v: ID | T): ID => (typeof v === 'object' ? datasource.getValue(v) : (v as ID));

    // 处理不同情况下的 value
    const normalizedValue = multiple
      ? Array.isArray(value)
        ? value.map(toID)
        : []
      : value !== undefined
        ? toID(value as ID | T)
        : undefined;

    // 使用条件类型安全地处理 onChange
    const handleChange = (selected: ID | ID[] | null) => {
      if (!onChange) return;

      const mult = Boolean(multiple);
      const useEnt = Boolean(useEntity);

      if (selected === null) {
        // 处理清除操作
        onChange((mult ? [] : undefined) as any);
        return;
      }

      if (mult) {
        const ids = selected as ID[];
        const mapped = useEnt ? ids.map(id => valueMap.get(id) as T) : ids;
        (onChange as any)(mapped);
      } else {
        const id = selected as ID;
        const mapped = useEnt ? valueMap.get(id) : id;
        (onChange as any)(mapped);
      }
    };

    return (
      <Select
        allowClear
        style={selectStyle}
        className={selectClassName}
        optionFilterProp="label"
        mode={multiple ? 'multiple' : undefined}
        value={normalizedValue}
        onChange={handleChange}
        options={options}
        loading={loading}
        placeholder={placeholder}
        getPopupContainer={v => v.parentNode}
      />
    );
  };
};
